import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import analyticsAPI from '@/lib/api/analytics';
import { detectBrowserName, detectDeviceType } from '@/lib/utils/analytics';

const SESSION_ID_KEY = '__analytics_session_id__';
const SESSION_CREATED_KEY = '__analytics_session_created__';
const HEARTBEAT_INTERVAL_MS = 60_000; // 60 seconds
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

type EndReason = 'logout' | 'tab_close' | 'timeout' | 'visibility_hidden' | 'navigation';

/**
 * Hook for managing analytics session lifecycle.
 * Handles:
 * - Session creation on mount
 * - Heartbeat every 60s to keep session active
 * - Session end on tab close (beforeunload)
 * - Session pause/resume on visibility change
 * - Inactivity timeout after 30 minutes
 */
export function useSessionLifecycle() {
  const { user } = useAuth();
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inactivityTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isSessionActiveRef = useRef<boolean>(false);

  // Get or create session ID
  const getSessionId = useCallback((): string | null => {
    if (typeof window === 'undefined') return null;
    let sid = sessionStorage.getItem(SESSION_ID_KEY);
    if (!sid) {
      sid = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_ID_KEY, sid);
    }
    return sid;
  }, []);

  // Create session if not exists
  const ensureSession = useCallback(async () => {
    if (!user) return;

    const sid = getSessionId();
    if (!sid) return;

    // Only create once per tab
    if (sessionStorage.getItem(SESSION_CREATED_KEY)) {
      isSessionActiveRef.current = true;
      return;
    }

    try {
      await analyticsAPI.sessions.create({
        session_id: sid,
        device_type: detectDeviceType(navigator.userAgent),
        browser: detectBrowserName(navigator.userAgent),
        referrer: document.referrer ?? '',
      });
      sessionStorage.setItem(SESSION_CREATED_KEY, '1');
      isSessionActiveRef.current = true;
    } catch {
      // Silent fail for analytics
    }
  }, [user, getSessionId]);

  // Send heartbeat to keep session alive
  const sendHeartbeat = useCallback(async () => {
    if (!user || !isSessionActiveRef.current) return;

    const sid = getSessionId();
    if (!sid) return;

    try {
      await analyticsAPI.sessions.heartbeat(sid);
    } catch {
      // Silent fail for analytics
    }
  }, [user, getSessionId]);

  // End session with reason
  const endSession = useCallback(async (reason: EndReason) => {
    if (!isSessionActiveRef.current) return;

    const sid = getSessionId();
    if (!sid) return;

    isSessionActiveRef.current = false;

    // Use sendBeacon for reliable delivery on page unload
    if (reason === 'tab_close' || reason === 'navigation') {
      // Try sendBeacon first (works during unload) - use beacon endpoint that doesn't require auth header
      const beaconUrl = `/api/v1/analytics/sessions/${encodeURIComponent(sid)}/end/beacon?end_reason=${encodeURIComponent(reason)}`;
      const sent = navigator.sendBeacon(beaconUrl);

      if (!sent) {
        // Fallback to regular request
        try {
          await analyticsAPI.sessions.end(sid, reason);
        } catch {
          // Silent fail
        }
      }
    } else {
      // Regular end for other reasons
      try {
        await analyticsAPI.sessions.end(sid, reason);
      } catch {
        // Silent fail
      }
    }

    // Clear session created flag so a new session can be created
    sessionStorage.removeItem(SESSION_CREATED_KEY);
  }, [getSessionId]);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    lastActivityRef.current = Date.now();

    if (inactivityTimeoutRef.current) {
      clearTimeout(inactivityTimeoutRef.current);
    }

    inactivityTimeoutRef.current = setTimeout(() => {
      if (isSessionActiveRef.current) {
        void endSession('timeout');
      }
    }, INACTIVITY_TIMEOUT_MS);
  }, [endSession]);

  // Handle visibility change
  const handleVisibilityChange = useCallback(() => {
    if (document.hidden) {
      // Tab is hidden - pause session
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
    } else {
      // Tab is visible - resume session
      if (user && isSessionActiveRef.current) {
        void sendHeartbeat();
        heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
        resetInactivityTimer();
      }
    }
  }, [user, sendHeartbeat, resetInactivityTimer]);

  // Handle beforeunload (tab close)
  const handleBeforeUnload = useCallback(() => {
    void endSession('tab_close');
  }, [endSession]);

  // Handle user activity
  const handleUserActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  // Main effect for session lifecycle
  useEffect(() => {
    if (!user) {
      // No user - clean up
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }
      return;
    }

    // Ensure session exists
    void ensureSession();

    // Start heartbeat interval
    heartbeatIntervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    // Start inactivity timer
    resetInactivityTimer();

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Activity listeners for inactivity detection
    const activityEvents = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach(event => {
      window.addEventListener(event, handleUserActivity, { passive: true });
    });

    return () => {
      // Cleanup
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
        inactivityTimeoutRef.current = null;
      }

      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      activityEvents.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [user, ensureSession, sendHeartbeat, resetInactivityTimer, handleBeforeUnload, handleVisibilityChange, handleUserActivity]);

  return {
    getSessionId,
    endSession,
  };
}

export default useSessionLifecycle;
